import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import useSWR from "swr";


import classes from "./HomeNewBanner.module.scss";
import PageLoader from "../../common/PageLoader";

const videoExtensions = [".mp4", ".webm", ".ogg"];
const AUTOPLAY_INTERVAL = 500000;
const MOBILE_BREAKPOINT = 768;

const decodeFirstImage = (slides) =>
  new Promise((resolve) => {
    if (slides.length === 0 || slides[0].type === 2) {
      resolve();
      return;
    }
    const img = new Image();
    img.src = slides[0].media;
    if (img.decode) {
      img
        .decode()
        .then(resolve)
        .catch(resolve);
    } else {
      img.onload = resolve;
      img.onerror = resolve;
    }
  });

const fetchBanners = async () => {
  const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
  const response = await axios.get(global.postUrl + "banners", {
    params: { page: "home" },
  });
  const data = response.data;

  if (data.success && data.banners) {
    const bannerList = isMobile ? data.banners.mobile : data.banners.desktop;
    const mapped = (bannerList || []).map((b) => ({
      media: b.banner,
      title: b.title,
      link: b.url,
      btnText: "Shop Now",
      type: b.type,
      thumbnail: b.thumbnail,
    }));
    await decodeFirstImage(mapped);
    return mapped;
  }
  return [];
};

const HomeNewBanner = () => {
  const isMobile =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT;
  const { data, error } = useSWR(
    `home-banners-${isMobile ? "mobile" : "desktop"}`,
    fetchBanners,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateIfStale: false,
      dedupingInterval: Infinity,
      shouldRetryOnError: false,
    },
  );
  const slides = data || [];
  // Only "loading" until the request settles. A failed or empty banner fetch
  // must not leave the loader up — the rest of the page still has to render.
  const isLoadingBanners = !data && !error;
  const [activeIndex, setActiveIndex] = useState(0);
  const timerRef = useRef(null);
  const dragStartX = useRef(null);
  const isDragging = useRef(false);

  const slidesLengthRef = useRef(slides.length);
  slidesLengthRef.current = slides.length;

  const startAutoPlay = useCallback(() => {
    clearInterval(timerRef.current);
    if (slides.length === 0) return;
    timerRef.current = setInterval(() => {
      const len = slidesLengthRef.current;
      if (len === 0) return;
      setActiveIndex((prev) => (prev + 1) % len);
    }, AUTOPLAY_INTERVAL);
  }, [slides.length]);

  useEffect(() => {
    startAutoPlay();
    return () => clearInterval(timerRef.current);
  }, [startAutoPlay]);

  const goToSlide = (index) => {
    setActiveIndex(index);
    startAutoPlay();
  };

  const goPrev = () => {
    if (slides.length === 0) return;
    setActiveIndex((prev) => (prev - 1 + slides.length) % slides.length);
    startAutoPlay();
  };

  const goNext = () => {
    if (slides.length === 0) return;
    setActiveIndex((prev) => (prev + 1) % slides.length);
    startAutoPlay();
  };

  const handleDragStart = (clientX) => {
    dragStartX.current = clientX;
    isDragging.current = true;
  };

  const handleDragEnd = (clientX) => {
    if (!isDragging.current || dragStartX.current === null) return;
    const diff = dragStartX.current - clientX;
    if (Math.abs(diff) > 50) {
      diff > 0 ? goNext() : goPrev();
    }
    dragStartX.current = null;
    isDragging.current = false;
  };

  const dragHandlers = {
    onMouseDown: (e) => handleDragStart(e.clientX),
    onMouseUp: (e) => handleDragEnd(e.clientX),
    onMouseLeave: () => {
      isDragging.current = false;
      dragStartX.current = null;
    },
    onTouchStart: (e) => handleDragStart(e.touches[0].clientX),
    onTouchEnd: (e) => handleDragEnd(e.changedTouches[0].clientX),
  };

  const isVideo = (slide) =>
    slide.type === 2 ||
    videoExtensions.some((ext) => (slide.media || "").endsWith(ext));

  const safeIndex =
    slides.length > 0 && activeIndex >= 0 && activeIndex < slides.length
      ? activeIndex
      : 0;

  if (isLoadingBanners) {
    return <PageLoader />;
  }

  // Banners failed to load or came back empty — render nothing rather than
  // holding the whole home page behind a spinner that will never resolve.
  if (slides.length === 0) {
    return null;
  }

  return (
    <section className={classes.bannerSection}>
      <div className={classes.slideWrapper} {...dragHandlers}>
        {slides.map((slide, index) => (
          <div
            key={index}
            className={`${classes.slideItem} ${index === safeIndex ? classes.active : ""
              }`}
          >
            {/* Media Background */}
            <div className={classes.mediaBg}>
              {isVideo(slide) ? (
                <video
                  className={classes.video}
                  autoPlay
                  loop
                  muted
                  playsInline
                  poster={slide.thumbnail}
                >
                  <source src={slide.media} />
                </video>
              ) : (
                <img
                  src={slide.media}
                  alt={slide.title}
                  loading={index === 0 ? "eager" : "lazy"}
                  fetchpriority={index === 0 ? "high" : "low"}
                  decoding={index === 0 ? "sync" : "async"}
                />
              )}
            </div>

            {/* Overlay */}
            <div className={classes.overlay} />

            {/* Content */}
            <div className={classes.content}>
              <div className="custom-container">
                <div className={classes.banner_content}>
                  {slide.title &&
                    <h1
                      className={classes.title}
                      style={{ whiteSpace: "pre-line" }}
                    >
                      {slide.title}
                    </h1>
                  }
                  {slide.btnText &&
                    <a href={slide.link} className={classes.btn}>
                      {slide.btnText}
                    </a>
                  }
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Navigation Arrows */}
      <div className={classes.arrow_wrapper}>
        <div className="custom-container">
          <div className={classes.arrows}>
            <button
              className={classes.arrowBtn}
              onClick={goPrev}
              aria-label="Previous slide"
            >
              <svg
                width="52"
                height="52"
                viewBox="0 0 52 52"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1265_34002)">
                  <path
                    d="M26.0053 51.5304C11.9023 51.5304 0.469589 40.0977 0.469589 25.9947C0.469589 11.8917 11.9023 0.458984 26.0053 0.458984C40.1083 0.458984 51.541 11.8917 51.541 25.9947C51.541 40.0977 40.1083 51.5304 26.0053 51.5304Z"
                    stroke="white"
                    strokeOpacity="0.5"
                  />
                  <path
                    d="M24.4849 19.6971C24.5686 19.4267 24.8557 19.2753 25.1261 19.3589C25.3966 19.4426 25.548 19.7297 25.4643 20.0001L24.9746 19.8486L24.4849 19.6971ZM19.3362 25.9997V26.5122H17.5917L19.0589 25.5686L19.3362 25.9997ZM19.3362 25.9997V25.4871H34.2012V25.9997V26.5122H19.3362V25.9997ZM21.2254 24.7845L21.5027 25.2156L19.6135 26.4308L19.3362 25.9997L19.0589 25.5686L20.9481 24.3534L21.2254 24.7845ZM24.9746 19.8486L25.4643 20.0001C24.799 22.1508 23.3961 23.9977 21.5027 25.2156L21.2254 24.7845L20.9481 24.3534C22.6385 23.2661 23.891 21.6172 24.4849 19.6971L24.9746 19.8486Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M21.8107 28.999C22.9775 29.8761 23.9053 31.0327 24.508 32.3623C24.6248 32.6201 24.9289 32.734 25.1867 32.6172C25.4442 32.5002 25.5582 32.1971 25.4416 31.9395C24.9417 30.8367 24.2416 29.8404 23.381 28.999H21.8107Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1265_34002">
                    <rect
                      width="52"
                      height="52"
                      fill="white"
                      transform="matrix(-1 0 0 1 52 0)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </button>
            <button
              className={classes.arrowBtn}
              onClick={goNext}
              aria-label="Next slide"
            >
              <svg
                width="52"
                height="52"
                viewBox="0 0 52 52"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_1265_34003)">
                  <path
                    d="M25.9947 51.5304C40.0977 51.5304 51.5304 40.0977 51.5304 25.9947C51.5304 11.8917 40.0977 0.458984 25.9947 0.458984C11.8917 0.458984 0.458984 11.8917 0.458984 25.9947C0.458984 40.0977 11.8917 51.5304 25.9947 51.5304Z"
                    stroke="white"
                    strokeOpacity="0.5"
                  />
                  <path
                    d="M27.5151 19.6971C27.4314 19.4267 27.1443 19.2753 26.8739 19.3589C26.6034 19.4426 26.452 19.7297 26.5357 20.0001L27.0254 19.8486L27.5151 19.6971ZM32.6638 25.9997V26.5122H34.4083L32.9411 25.5686L32.6638 25.9997ZM32.6638 25.9997V25.4871H17.7988V25.9997V26.5122H32.6638V25.9997ZM30.7746 24.7845L30.4973 25.2156L32.3865 26.4308L32.6638 25.9997L32.9411 25.5686L31.0519 24.3534L30.7746 24.7845ZM27.0254 19.8486L26.5357 20.0001C27.201 22.1508 28.6039 23.9977 30.4973 25.2156L30.7746 24.7845L31.0519 24.3534C29.3615 23.2661 28.109 21.6172 27.5151 19.6971L27.0254 19.8486Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M30.1893 28.999C29.0225 29.8761 28.0947 31.0327 27.492 32.3623C27.3752 32.6201 27.0711 32.734 26.8133 32.6172C26.5558 32.5002 26.4418 32.1971 26.5584 31.9395C27.0583 30.8367 27.7584 29.8404 28.619 28.999H30.1893Z"
                    fill="white"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_1265_34003">
                    <rect width="52" height="52" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomeNewBanner;
